import {
  type AnalyticsNavigationObserver,
  type AnalyticsRoute,
  getAnalyticsNavigation,
  readAnalyticsRoute
} from './analyticsNavigation'

export const ANALYTICS_FRAME_OWNER_ATTRIBUTE = 'data-container-analytics-owner'
export const ANALYTICS_OWNER_REQUEST = 'CONTAINER_ANALYTICS_OWNER_REQUEST'
export const ANALYTICS_OWNER_RESPONSE = 'CONTAINER_ANALYTICS_OWNER_RESPONSE'

export interface AnalyticsFrame {
  id: string
  appId: string
  title: string
  iframe: HTMLIFrameElement
  visible: boolean
  active: boolean
  route: AnalyticsRoute | null
  documentVersion: number
  activityVersion: number
}

const frames = new Map<HTMLIFrameElement, AnalyticsFrame>()
const listeners = new Set<() => void>()
let snapshot: AnalyticsFrame[] = []
const publish = () => {
  snapshot = [...frames.values()]
  listeners.forEach(listener => listener())
}
const onDocumentFocus = () => {
  frames.forEach((frame, iframe) => {
    frames.set(iframe, { ...frame, activityVersion: frame.activityVersion + 1 })
  })
  publish()
}

export const getAnalyticsFrames = () => snapshot
export const subscribeAnalyticsFrames = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const updateAnalyticsFrame = (
  iframe: HTMLIFrameElement,
  state: Pick<AnalyticsFrame, 'visible' | 'active'>
) => {
  const frame = frames.get(iframe)
  if (
    !frame ||
    (frame.visible === state.visible && frame.active === state.active)
  )
    return
  frames.set(iframe, {
    ...frame,
    ...state,
    activityVersion: frame.activityVersion + 1
  })
  publish()
}

// Same-origin frames whose history this module observes: the document being
// observed, and whether it is leaving.
const observedFrames = new Map<
  HTMLIFrameElement,
  {
    departed: boolean
    observer: AnalyticsNavigationObserver
    document: Document
  }
>()
// Starts observing a frame's first app document at a route its bridge reported
// before that document's `load`.
const frameConnectors = new Map<HTMLIFrameElement, (href: string) => void>()
// Cross-origin frames can't be told apart by document, only by `load` count.
// Counts app documents only, not the initial about:blank.
const frameLoads = new Map<HTMLIFrameElement, number>()
const bridgeRoutedBeforeFirstLoad = new Set<HTMLIFrameElement>()

const readFrameDocument = (iframe: HTMLIFrameElement) => {
  try {
    return iframe.contentDocument
  } catch {
    return null
  }
}

const applyFrameRoute = (iframe: HTMLIFrameElement, href: string) => {
  const frame = frames.get(iframe)
  if (!frame) return
  const next = readAnalyticsRoute(
    href,
    frame.route?.location,
    (frame.route?.revision ?? 0) + 1
  )
  if (next.path === frame.route?.path) return
  frames.set(iframe, { ...frame, route: next })
  publish()
}

// Called from the existing, frame-specific container-bridge callback. An MFE
// with a memory router can therefore reuse its existing navigation integration.
export const reportAnalyticsFrameRoute = (
  iframe: HTMLIFrameElement,
  path: string
) => {
  if (!frames.has(iframe) || !path) return
  let href: string
  try {
    const base = new URL(iframe.src, window.location.href)
    const nextUrl = new URL(path, base)
    if (
      base.origin !== nextUrl.origin ||
      !['http:', 'https:'].includes(nextUrl.protocol)
    )
      return
    href = nextUrl.href
  } catch {
    // Malformed or inaccessible frame navigation cannot affect the app.
    return
  }
  const observed = observedFrames.get(iframe)
  // A departing document's late report must not land on the next page, even
  // once that page's document is in the frame.
  if (observed?.departed) return
  const frameDocument = readFrameDocument(iframe)
  // A document that isn't observed yet reported before its `load` event, which
  // is when it would otherwise connect.
  if (frameDocument && frameDocument !== observed?.document) {
    frameConnectors.get(iframe)?.(href)
    return
  }
  // While a browser-URL navigation is in flight, the report is its echo: the
  // frame's history observer counts it and folds its redirect. Otherwise it's a
  // bridge-only (memory router) navigation, even back to the URL's own path.
  if (observed?.observer.isNavigating()) return
  // A cross-origin frame's first `load` then keeps this route.
  if (!frameDocument && !frameLoads.get(iframe))
    bridgeRoutedBeforeFirstLoad.add(iframe)
  applyFrameRoute(iframe, href)
}

export const registerAnalyticsFrame = (
  initial: Omit<AnalyticsFrame, 'route' | 'documentVersion' | 'activityVersion'>
) => {
  const { iframe } = initial
  let documentVersion = 0
  // Released once the frame leaves it (about:blank, another origin), so a
  // long-lived window doesn't retain a detached DOM. `hadDocument` remembers.
  let currentDocument: Document | null = null
  let hadDocument = false
  let stopObserving: (() => void) | undefined
  let active = true
  const previousOwner = iframe.getAttribute(ANALYTICS_FRAME_OWNER_ATTRIBUTE)
  iframe.setAttribute(ANALYTICS_FRAME_OWNER_ATTRIBUTE, 'parent')
  frames.set(iframe, {
    ...initial,
    route: null,
    documentVersion,
    activityVersion: 0
  })
  if (frames.size === 1) document.addEventListener('focusin', onDocumentFocus)

  const activity = () => {
    const frame = frames.get(iframe)
    if (!frame) return
    frames.set(iframe, { ...frame, activityVersion: frame.activityVersion + 1 })
    publish()
  }
  const connect = (loaded = false, bridgeHref?: string) => {
    if (!active) return
    // A document observed before its `load` event keeps its observer.
    // Restarting it would drop a navigation still settling and the bridge's
    // route.
    if (stopObserving && readFrameDocument(iframe) === currentDocument) return
    const stopPrevious = stopObserving
    stopObserving = undefined
    stopPrevious?.()
    let child: Window | null = null
    try {
      child = iframe.contentWindow
      if (!child || !child.document || child.document.URL === 'about:blank') {
        currentDocument = null
        return
      }
      const childDocument = child.document
      if (childDocument !== currentDocument) {
        if (hadDocument) documentVersion += 1
        currentDocument = childDocument
        hadDocument = true
      }
      const observer = getAnalyticsNavigation(child)
      const observed = { departed: false, observer, document: childDocument }
      const update = () => {
        const frame = frames.get(iframe)
        if (!frame || observed.departed) return
        const route = observer.getSnapshot()
        // The bridge may already have reported this path for this document.
        if (
          frame.documentVersion === documentVersion &&
          frame.route?.path === route.path
        )
          return
        frames.set(iframe, { ...frame, documentVersion, route })
        publish()
      }
      const onPageHide = () => {
        observed.departed = true
        const frame = frames.get(iframe)
        if (!frame) return
        frames.set(iframe, { ...frame, route: null })
        publish()
      }
      const onPageShow = (event: PageTransitionEvent) => {
        if (!event.persisted) return
        observed.departed = false
        documentVersion += 1
        update()
      }
      const unsubscribe = observer.subscribe(update)
      child.addEventListener('focus', activity)
      child.addEventListener('blur', activity)
      child.addEventListener('pagehide', onPageHide)
      child.addEventListener('pageshow', onPageShow)
      observedFrames.set(iframe, observed)
      stopObserving = () => {
        if (observedFrames.get(iframe) === observed)
          observedFrames.delete(iframe)
        unsubscribe()
        // A frame that navigated to another origin blocks these calls; its old
        // document took the listeners with it.
        try {
          child?.removeEventListener('focus', activity)
          child?.removeEventListener('blur', activity)
          child?.removeEventListener('pagehide', onPageHide)
          child?.removeEventListener('pageshow', onPageShow)
        } catch {
          /* Nothing left to remove across origins. */
        }
      }
      const frame = frames.get(iframe)
      if (bridgeHref && frame) {
        // The bridge reported where this document is; a memory router's URL
        // doesn't show it.
        const { referrer, revision } = observer.getSnapshot()
        frames.set(iframe, {
          ...frame,
          documentVersion,
          route: readAnalyticsRoute(bridgeHref, referrer, revision)
        })
        publish()
      } else update()
    } catch {
      // Cross-origin pages expose their initial src and may expose later routes
      // through the existing bridge. Never inspect or inject across origins.
      currentDocument = null
      if (!loaded) return
      const frame = frames.get(iframe)
      if (!frame) return
      const firstDocument = frameLoads.get(iframe) === 1 && !hadDocument
      const bridgedBeforeLoad = bridgeRoutedBeforeFirstLoad.delete(iframe)
      // Its src is where the first document started, and the bridge already
      // reported where it is now.
      if (firstDocument && bridgedBeforeLoad && frame.route) return
      try {
        const route = readAnalyticsRoute(iframe.src)
        if (!firstDocument) documentVersion += 1
        frames.set(iframe, { ...frame, documentVersion, route })
        publish()
      } catch {
        /* Invalid frame URL: wait for a valid bridge route. */
      }
    }
  }
  const onLoad = () => {
    // Safari also fires `load` for the initial about:blank document.
    if (readFrameDocument(iframe)?.URL !== 'about:blank')
      frameLoads.set(iframe, (frameLoads.get(iframe) ?? 0) + 1)
    connect(true)
  }
  frameConnectors.set(iframe, href => {
    // Only the frame's first app document starts at a report, observed from
    // then on. A report can't say which document sent it, so a later document
    // might be receiving its predecessor's; it starts at its URL on `load`.
    if (!hadDocument && !frameLoads.get(iframe)) connect(false, href)
  })
  const onMessage = (event: MessageEvent<unknown>) => {
    if (event.source !== iframe.contentWindow) return
    let expectedOrigin: string
    try {
      expectedOrigin = new URL(iframe.src, window.location.href).origin
    } catch {
      return
    }
    if (event.origin !== expectedOrigin) return
    const data = event.data
    if (!data || typeof data !== 'object' || !('type' in data)) return
    if (data.type === ANALYTICS_OWNER_REQUEST) {
      iframe.contentWindow?.postMessage(
        { type: ANALYTICS_OWNER_RESPONSE, owned: true },
        expectedOrigin
      )
    }
  }
  iframe.addEventListener('load', onLoad)
  window.addEventListener('message', onMessage)
  connect()
  publish()
  return () => {
    active = false
    stopObserving?.()
    frameConnectors.delete(iframe)
    frameLoads.delete(iframe)
    bridgeRoutedBeforeFirstLoad.delete(iframe)
    iframe.removeEventListener('load', onLoad)
    window.removeEventListener('message', onMessage)
    if (previousOwner === null)
      iframe.removeAttribute(ANALYTICS_FRAME_OWNER_ATTRIBUTE)
    else iframe.setAttribute(ANALYTICS_FRAME_OWNER_ATTRIBUTE, previousOwner)
    frames.delete(iframe)
    if (!frames.size) document.removeEventListener('focusin', onDocumentFocus)
    publish()
  }
}

export const isAnalyticsFrameForeground = (iframe: HTMLIFrameElement) => {
  const frame = frames.get(iframe)
  return Boolean(
    frame?.active &&
      frame.visible &&
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      document.activeElement === iframe
  )
}
